'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const DSP_COLORS: Record<string, string> = {
  'Spotify': '#1DB954',
  'Apple Music': '#FC3C44',
  'YouTube Music': '#FF0000',
  'Amazon Music': '#00A8E1',
  'Tidal': '#00FFFF',
  'JioSaavn': '#2BC5B4',
  'Gaana': '#E72C30',
  'Wynk': '#7B2FBE',
};

const STATUS_CONFIG = {
  delivered: { label: 'Live', color: 'var(--success)', bg: 'var(--success-bg)', icon: 'CheckCircleIcon' },
  processing: { label: 'Processing', color: 'var(--warning)', bg: 'var(--warning-bg)', icon: 'ClockIcon' },
  failed: { label: 'Failed', color: 'var(--danger)', bg: 'rgba(239,68,68,0.12)', icon: 'XCircleIcon' },
  draft: { label: 'Draft', color: 'var(--muted-foreground)', bg: 'var(--muted)', icon: 'DocumentIcon' },
};

interface DspAvailability {
  dsp: string;
  available: boolean;
  streams: number;
}

interface ReleaseRow {
  id: string;
  title: string;
  releaseType: string;
  status: string;
  streams30d: number;
  totalStreams: number;
  totalEarnings: number;
  createdAt: string;
  dspAvailability: DspAvailability[];
  deliveryStatus: 'delivered' | 'processing' | 'failed' | 'draft';
}

interface DspDeliveryTableProps {
  releases: ReleaseRow[];
}

export default function DspDeliveryTable({ releases }: DspDeliveryTableProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!releases.length) {
    return (
      <div className="text-center py-16">
        <Icon name="MusicalNoteIcon" size={40} className="mx-auto mb-4" style={{ color: 'var(--muted-foreground)' }} />
        <p className="text-base font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No releases yet</p>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Create a release to see DSP delivery status.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {releases.map((rel) => {
        const statusCfg = STATUS_CONFIG[rel.deliveryStatus] || STATUS_CONFIG.draft;
        const isExpanded = expanded === rel.id;
        const availableDsps = rel.dspAvailability.filter((d) => d.available);

        return (
          <div
            key={rel.id}
            className="rounded-xl overflow-hidden"
            style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}
          >
            {/* Row header */}
            <button
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/5 transition-colors"
              onClick={() => setExpanded(isExpanded ? null : rel.id)}
            >
              {/* Release info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{rel.title}</p>
                  <span
                    className="text-2xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0"
                    style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                  >
                    {rel.releaseType}
                  </span>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {new Date(rel.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>

              {/* DSP dots preview */}
              <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                {rel.dspAvailability.slice(0, 6).map((d) => (
                  <div
                    key={d.dsp}
                    className="w-2.5 h-2.5 rounded-full"
                    title={`${d.dsp}: ${d.available ? 'Available' : 'Not available'}`}
                    style={{
                      background: d.available ? (DSP_COLORS[d.dsp] || 'var(--success)') : 'var(--border)',
                      opacity: d.available ? 1 : 0.4,
                    }}
                  />
                ))}
                <span className="text-xs ml-1" style={{ color: 'var(--muted-foreground)' }}>
                  {availableDsps.length}/{rel.dspAvailability.length}
                </span>
              </div>

              {/* Streams */}
              <div className="text-right flex-shrink-0 hidden md:block">
                <p className="text-sm font-bold font-mono-data" style={{ color: 'var(--primary-light)' }}>
                  {Number(rel.streams30d).toLocaleString('en-IN')}
                </p>
                <p className="text-2xs" style={{ color: 'var(--muted-foreground)' }}>30d streams</p>
              </div>

              {/* Status badge */}
              <div
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-shrink-0"
                style={{ background: statusCfg.bg }}
              >
                <Icon name={statusCfg.icon as any} size={13} style={{ color: statusCfg.color }} />
                <span className="text-xs font-semibold" style={{ color: statusCfg.color }}>{statusCfg.label}</span>
              </div>

              {/* Expand icon */}
              <Icon
                name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                size={16}
                style={{ color: 'var(--muted-foreground)', flexShrink: 0 }}
              />
            </button>

            {/* Expanded DSP grid */}
            {isExpanded && (
              <div className="px-5 pb-5 pt-1" style={{ borderTop: '1px solid var(--border)' }}>
                <p className="text-xs font-semibold uppercase tracking-wide mb-3 mt-3" style={{ color: 'var(--muted-foreground)' }}>
                  DSP Availability & Store Delivery
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {rel.dspAvailability.map((d) => (
                    <div
                      key={d.dsp}
                      className="flex items-center gap-2.5 p-3 rounded-lg"
                      style={{
                        background: d.available ? 'rgba(255,255,255,0.04)' : 'transparent',
                        border: `1px solid ${d.available ? 'var(--border-light)' : 'var(--border)'}`,
                        opacity: d.available ? 1 : 0.5,
                      }}
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ background: d.available ? (DSP_COLORS[d.dsp] || 'var(--success)') : 'var(--border)' }}
                      />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: 'var(--foreground)' }}>{d.dsp}</p>
                        {d.available ? (
                          <p className="text-2xs font-mono-data" style={{ color: 'var(--success)' }}>
                            {d.streams > 0 ? `${Number(d.streams).toLocaleString('en-IN')} streams` : 'Live'}
                          </p>
                        ) : (
                          <p className="text-2xs" style={{ color: 'var(--muted-foreground)' }}>Not available</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Earnings summary */}
                {rel.totalEarnings > 0 && (
                  <div className="mt-4 flex items-center gap-6 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                    <div>
                      <p className="text-lg font-bold font-mono-data" style={{ color: 'var(--warning)' }}>
                        ₹{Number(rel.totalEarnings).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Total Earnings</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold font-mono-data" style={{ color: 'var(--primary-light)' }}>
                        {Number(rel.totalStreams).toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>All-time Streams</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
